import { useWorkspace } from '../workspace/WorkspaceProvider';
import { isWorkspaceAdmin } from '../api/types';

/**
 * Whether the signed-in user administers the active workspace.
 *
 * UX gating only, and the same caveat as {@link useCanWrite} applies: appcore checks
 * capabilities on every request, and `RequireAdmin` guards the admin routes themselves.
 * What this buys is a portal that doesn't point people at doors they cannot open.
 *
 * Lived as a private helper in `Settings.tsx`, plus five inline copies of the identical
 * expression, until the Contacts signpost needed it too (steward-app#580). All of them
 * now call this: an extraction that leaves the copies behind is worse than none, because
 * the next role-rule change updates the hook and silently misses everywhere else.
 *
 * `RequireAdmin` deliberately does NOT use it — it distinguishes "no membership yet"
 * (render nothing) from "not an admin" (redirect), which one boolean cannot express.
 *
 * No `meLoaded` guard, and none is needed: `activeMembership` is `null` until /me
 * resolves, so this reads `false` during the load window and can only ever flip
 * `false → true` once a real admin membership arrives. That is fail-CLOSED, the opposite
 * direction from {@link useCanWrite}, which fails open on purpose because stripping a
 * writer of every control is worse than a viewer meeting appcore's 403. Do not "align"
 * the two: an admin surface shown to a non-admin is the costly error here, so the
 * asymmetry is the point.
 */
export function useIsAdmin(): boolean {
  const { activeMembership } = useWorkspace();
  return !!activeMembership && isWorkspaceAdmin(activeMembership.roleIds);
}
